import type { ModuleFederationRuntimePlugin } from '@module-federation/enhanced/runtime';
import { DomainCode, logger } from '@parrot-software/observability';

type SharedGuardPluginOptions = {
  /** Versions the host provides, keyed by package name. Packages outside the map are not checked. */
  hostVersions: Readonly<Record<string, string>>;
  /** Observability domain of a registered remote, or undefined for the host and unknown remotes. */
  resolveDomain: (remoteName: string) => DomainCode | undefined;
};

type ResolveShareArgs = Parameters<NonNullable<ModuleFederationRuntimePlugin['resolveShare']>>[0];
type ErrorLoadRemoteArgs = Parameters<NonNullable<ModuleFederationRuntimePlugin['errorLoadRemote']>>[0];

const errorKind = (error: unknown): string =>
  error instanceof Error ? error.name || error.constructor.name : 'Unknown';

/**
 * Runtime plugin that makes shared-dependency drift visible: it warns `mfeSharedVersionMismatch`
 * when a remote asks for, or ends up with, a version of a host singleton other than the host's.
 * It also records at which lifecycle stage a remote failed to load, then lets the error propagate
 * so the UI layer shows its error view.
 *
 * It must also be registered as a global plugin: each remote resolves its shares in its own
 * federation instance, which only inherits global plugins.
 */
export const createSharedGuardPlugin = ({
  hostVersions,
  resolveDomain,
}: SharedGuardPluginOptions): ModuleFederationRuntimePlugin => {
  const reported = new Set<string>();

  return {
    errorLoadRemote: ({ error, id, lifecycle, remote }: ErrorLoadRemoteArgs) => {
      const remoteName = remote?.name ?? id.split('/')[0];
      const domain = resolveDomain(remoteName);
      if (domain) {
        logger.forDomain(domain).warn('mfeRemoteLifecycle', {
          'error.kind': errorKind(error),
          'error.message': error instanceof Error ? error.message : String(error),
          lifecycle,
          remote: id,
        });
      }
      return undefined;
    },
    name: 'parrot-shared-guard',
    resolveShare: (args: ResolveShareArgs) => {
      const { pkgName, resolver, shareInfo } = args;
      const hostVersion = hostVersions[pkgName];
      const remoteName = shareInfo.from;
      const domain = resolveDomain(remoteName);
      if (!hostVersion || !domain) {
        return args;
      }

      return {
        ...args,
        resolver: () => {
          const resolved = resolver();
          const resolvedVersion = resolved?.shared.version;
          const reportKey = `${remoteName}:${pkgName}:${shareInfo.version}:${resolvedVersion}`;
          const drifted = shareInfo.version !== hostVersion || resolvedVersion !== hostVersion;
          if (drifted && !reported.has(reportKey)) {
            reported.add(reportKey);
            logger.forDomain(domain).warn('mfeSharedVersionMismatch', {
              hostVersion,
              pkg: pkgName,
              remote: remoteName,
              remoteVersion: shareInfo.version,
              resolvedVersion: resolvedVersion ?? 'none',
            });
          }
          return resolved;
        },
      };
    },
  };
};
